import React, { useState, useEffect } from "react";
import API from "../../../api/api";
import { resolveAssetUrl } from "../../../api/config";
import {
  FaChartLine,
  FaSave,
  FaPrint,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaStopwatch,
  FaUser,
  FaUsers,
  FaTable,
  FaFileUpload,
  FaCloudUploadAlt,
} from "react-icons/fa";

import Swal from "sweetalert2";
import FileUploadWithScanner from "../../../components/common/FileUploadWithScanner";
import { downloadDomPagesAsPdf } from "../../../utils/pdfDownloadHelper";

/* ── Organization print header (logos + title): SSA -> PARASPORTS -> HEAL ── */
const PrintOrgHeader = ({ campName, venue }) => (
  <div style={{ borderBottom: "2px solid #0f172a", paddingBottom: "8px", marginBottom: "12px" }}>
    {/* Logo row */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
      {/* 1. SSA Logo (Left) */}
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <img
          src="/images/ssa.png"
          alt="Samagra Shiksha"
          style={{ height: "48px", width: "auto", objectFit: "contain" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
      <div style={{ textAlign: "center", flex: 1, padding: "0 8px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ fontSize: "13px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.04em", color: "#0f172a" }}>
          SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", margin: "2px 0" }}>
          <img
            src="/images/parasports.png"
            alt="Para Sports Logo"
            style={{ height: "24px", width: "auto", objectFit: "contain" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div style={{ fontSize: "13px", fontWeight: "900", textTransform: "uppercase", letterSpacing: "0.04em", color: "#0f172a" }}>
            PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
          </div>
        </div>
        <div style={{ fontSize: "8.5px", color: "#475569", fontWeight: "600" }}>
          Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
        </div>
      </div>

      {/* 3. Government of Andhra Pradesh Emblem (Right) */}
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <img
          src="/images/ap_govt.png"
          alt="Government of Andhra Pradesh"
          style={{ height: "46px", width: "auto", objectFit: "contain" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
    </div>
    {/* Title badge */}
    <div style={{ textAlign: "center", marginTop: "6px" }}>
      <span style={{ display: "inline-block", background: "#0f172a", color: "#fff", fontSize: "10px", fontWeight: "800", padding: "2px 14px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Coaching Camp — Performance Benchmark Assessment (Week 1 vs Week 9)
      </span>
      {campName && (
        <div style={{ fontSize: "10px", color: "#334155", fontWeight: "600", marginTop: "4px" }}>
          Camp: {campName}{venue ? ` • Venue: ${venue}` : ""} • Generated: {new Date().toLocaleDateString("en-GB")}
        </div>
      )}
    </div>
  </div>
);

const testDefinitions = [
  { key: "test_height", label: "Height", placeholder: "e.g. 168 cm", hint: "Height in cm" },
  { key: "test_weight", label: "Body Weight", placeholder: "e.g. 64.5kg", hint: "Weight in kilograms" },
  { key: "test_100m", label: "100m Sprint", placeholder: "e.g. 13.4s", hint: "Time in seconds" },
  { key: "test_400m", label: "400m Run", placeholder: "e.g. 62.1s", hint: "Time in seconds" },
  { key: "test_shot_put", label: "Shot Put – 5 kg", placeholder: "e.g. 9.4m", hint: "Best throw in meters" },
  { key: "test_long_jump", label: "Long Jump – 3 Jumps", placeholder: "e.g. 4.85m", hint: "Best distance in meters" },
  { key: "test_skipping", label: "Skipping – 3 Chances", placeholder: "e.g. 185", hint: "Total counts / reps" },
  { key: "test_pushups", label: "Push-ups – 3 Sets", placeholder: "e.g. 48", hint: "Total repetitions completed" },
  { key: "test_plank", label: "Plank – Max Timing", placeholder: "e.g. 2m 30s", hint: "Hold duration" },
  { key: "test_pullups", label: "Pull-ups – 3 Sets", placeholder: "e.g. 15", hint: "Total repetitions completed" },
  { key: "test_cycling_5km", label: "Cycling – 5 km", placeholder: "e.g. 8m 45s", hint: "Time duration" },
  { key: "test_swimming_50m", label: "Swimming – 50 m", placeholder: "e.g. 33.8s", hint: "Time duration" },
];

const inferStandardWeeklyTrialField = (gameName = "") => {
  const name = String(gameName).toLowerCase();
  if (/shot\s*put/.test(name)) return "test_shot_put";
  if (/long\s*jump/.test(name)) return "test_long_jump";
  if (/400\s*m/.test(name)) return "test_400m";
  if (/100\s*m|sprint/.test(name)) return "test_100m";
  if (/cycl/.test(name)) return "test_cycling_5km";
  if (/swim/.test(name)) return "test_swimming_50m";
  if (/skip/.test(name)) return "test_skipping";
  if (/push.?up/.test(name)) return "test_pushups";
  if (/pull.?up/.test(name)) return "test_pullups";
  if (/plank/.test(name)) return "test_plank";
  if (/weight|powerlift/.test(name)) return "test_weight";
  return "";
};

const getCustomAllottedGames = (athlete) => {
  const allocated = Array.isArray(athlete?.allocated_disciplines) ? athlete.allocated_disciplines : [];
  return allocated
    .map((game) => {
      const details = typeof game === "string" ? {} : game;
      const gameName = typeof game === "string" ? game : game?.name || game?.discipline_name || "";
      return {
        game_name: String(gameName).trim(),
        weekly_trial_field: details?.weekly_trial_field || inferStandardWeeklyTrialField(gameName) || "custom_game",
        measurement_type: details?.measurement_type || "other",
        measurement_unit: details?.measurement_unit || "",
      };
    })
    .filter((game) => game.game_name && game.weekly_trial_field === "custom_game");
};

const buildCustomGameTrials = (athlete, savedTrials = []) =>
  getCustomAllottedGames(athlete).map((game) => {
    const saved = Array.isArray(savedTrials)
      ? savedTrials.find((trial) => String(trial?.game_name || "").trim().toLowerCase() === game.game_name.toLowerCase())
      : null;
    return { ...game, value: saved?.value || "" };
  });

const CoachCampReports = () => {
  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState("");
  const [viewMode, setViewMode] = useState("individual"); // "individual" | "all_athletes"
  const [activeWeek, setActiveWeek] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allAthletesData, setAllAthletesData] = useState([]); // for pivot table
  const [loadingAll, setLoadingAll] = useState(false);
  const [activeCamp, setActiveCamp] = useState(null);

  const [formData, setFormData] = useState({
    ...Object.fromEntries([...testDefinitions.map((t) => [t.key, ""]), ["remarks", ""]]),
    game_trials: [],
  });
  const [comparisonData, setComparisonData] = useState(null);

  /* ── Load athletes on mount ── */
  useEffect(() => {
    const init = async () => {
      try {
        const [athleteRes, campRes] = await Promise.all([
          API.camp.coachPortal.getMyAthletes(),
          API.camp.coachPortal.getActiveCamp(),
        ]);
        const list = athleteRes?.data?.athletes || athleteRes?.athletes || [];
        setAthletes(list);
        if (list.length > 0) setSelectedAthleteId(String(list[0].id));
        setActiveCamp(campRes?.data?.camp || campRes?.camp || null);
      } catch (err) {
        console.error("Failed to load athletes:", err);
      }
    };
    init();
  }, []);

  /* ── Fetch individual comparison data ── */
  const fetchAssessmentData = async () => {
    if (!selectedAthleteId || viewMode !== "individual") return;
    setLoading(true);
    try {
      const [comparisonRes, reportsRes] = await Promise.all([
        API.camp.coachPortal.getPerformanceComparison(selectedAthleteId),
        API.camp.coachPortal.getPerformanceReports({ athlete_id: selectedAthleteId }),
      ]);
      const data = comparisonRes?.data || comparisonRes;
      const weeklyReports = reportsRes?.data?.reports || reportsRes?.reports || reportsRes?.data?.data || reportsRes?.data || [];
      setComparisonData(data);
      const target = Array.isArray(weeklyReports)
        ? weeklyReports.find((report) => Number(report.week_number) === Number(activeWeek))
        : null;
      const athlete = athletes.find((item) => String(item.id) === String(selectedAthleteId));
      if (target) {
        setFormData({
          ...Object.fromEntries([...testDefinitions.map((t) => [t.key, target[t.key] || ""]), ["remarks", target.remarks || ""]]),
          game_trials: buildCustomGameTrials(athlete, target.game_trials),
        });
      } else {
        setFormData({
          ...Object.fromEntries([...testDefinitions.map((t) => [t.key, ""]), ["remarks", ""]]),
          game_trials: buildCustomGameTrials(athlete),
        });
      }
    } catch (err) {
      console.error("Failed to load comparison:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAthleteId, activeWeek, viewMode]);

  /* ── Fetch all athletes data for pivot table ── */
  useEffect(() => {
    if (viewMode !== "all_athletes") return;
    const fetchAll = async () => {
      setLoadingAll(true);
      try {
        const res = await API.camp.coachPortal.getPerformanceReports();
        const data = res?.data?.data || res?.data || [];
        setAllAthletesData(data);
      } catch (err) {
        console.error("Failed to load all athletes data:", err);
      } finally {
        setLoadingAll(false);
      }
    };
    fetchAll();
  }, [viewMode]);

  const handleChange = (field, val) =>
    setFormData((prev) => ({ ...prev, [field]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedAthleteId) return;
    setSaving(true);
    try {
      const campId = activeCamp?.id || 1;
      const payload = { camp_id: campId, athlete_id: selectedAthleteId, week_number: activeWeek, ...formData };
      const res = await API.camp.coachPortal.savePerformanceReport(payload);
      if (res?.data?.success || res?.success) {
        Swal.fire("Saved", `Week ${activeWeek} assessment saved!`, "success");
        fetchAssessmentData();
      }
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Print handler — triggers printMode in hidden container ── */
  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  const handleDownload = () => downloadDomPagesAsPdf(
    ".print-only",
    "Performance_Benchmark_Assessment",
    { orientation: "landscape", pageSelector: ".print-page" }
  );

  const selectedAthlete = athletes.find((a) => String(a.id) === String(selectedAthleteId));
  const customAllottedGames = getCustomAllottedGames(selectedAthlete);
  const handleCustomGameTrialChange = (gameName, value) => {
    setFormData((previous) => ({
      ...previous,
      game_trials: buildCustomGameTrials(selectedAthlete, previous.game_trials).map((trial) =>
        trial.game_name.toLowerCase() === String(gameName).toLowerCase() ? { ...trial, value } : trial
      ),
    }));
  };
  const photoUrl = selectedAthlete?.photo ? resolveAssetUrl(selectedAthlete.photo) : null;

  /* ── Build pivot: athlete x test (Week 1 values) ── */
  const week1Records = allAthletesData.filter((p) => p.week_number === 1);
  const week9Records = allAthletesData.filter((p) => p.week_number === 9);
  const pivotAthletes = athletes;

  const getVal = (records, athleteId, key) => {
    const r = records.find((p) => String(p.athlete_id) === String(athleteId));
    return r?.[key] || "—";
  };

  // Report Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadWeek, setUploadWeek] = useState(1);
  const [uploadAthleteId, setUploadAthleteId] = useState("");
  const [uploadRemarks, setUploadRemarks] = useState("");
  const [uploadingReport, setUploadingReport] = useState(false);

  const handleUploadReportSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      Swal.fire("Missing File", "Please select a performance assessment report file (PDF/Image).", "warning");
      return;
    }
    const formData = new FormData();
    const campId = activeCamp?.id || 1;
    formData.append("camp_id", campId);
    if (uploadAthleteId) formData.append("athlete_id", uploadAthleteId);
    formData.append("week_number", uploadWeek);
    if (uploadRemarks) formData.append("remarks", uploadRemarks);
    formData.append("file", uploadFile);

    setUploadingReport(true);
    try {
      const res = await API.camp.coachPortal.uploadBenchmarkReport(formData);
      Swal.fire({
        icon: "success",
        title: "Report Uploaded!",
        text: res?.data?.message || "Assessment report document uploaded successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadRemarks("");
      fetchAssessmentData();
    } catch (err) {
      console.error("Report upload failed:", err);
      Swal.fire("Upload Failed", err?.response?.data?.message || "Failed to upload performance report.", "error");
    } finally {
      setUploadingReport(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Print CSS ── */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body { background: #fff !important; color: #000 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print-hidden { display: none !important; }
          .print-only { display: block !important; }
          @page { size: A4 landscape; margin: 8mm 10mm; }
          .print-page { page-break-after: always; break-after: page; }
          .print-page:last-child { page-break-after: auto; break-after: auto; }
        }
        .print-only { display: none; }
      `}} />

      {/* ── Header Banner ── */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm print-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
              Benchmark Assessments
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5 tracking-tight flex items-center gap-2">
              <FaChartLine className="text-cyan-700" /> Coaching Camp Performance Assessment
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Week 1 (Initial) vs Week 9 (Final) with Automated Improvement Tracking &amp; Height Measurements
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => {
                setUploadAthleteId(selectedAthleteId || (athletes[0]?.id ? String(athletes[0].id) : ""));
                setShowUploadModal(true);
              }}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
            >
              <FaFileUpload /> Upload Report
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
            >
              <FaDownload /> Download PDF
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
            >
              <FaPrint /> Print Report
            </button>
          </div>
        </div>
      </section>

      {/* ── Upload Report Modal ── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-emerald-700 to-cyan-800 text-white p-5 flex justify-between items-center">
              <h3 className="text-base font-bold flex items-center gap-2">
                <FaCloudUploadAlt /> Upload Performance Benchmark Report
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUploadReportSubmit} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Athlete (Optional / All Team)
                </label>
                <select
                  value={uploadAthleteId}
                  onChange={(e) => setUploadAthleteId(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Whole Team / General Report</option>
                  {athletes.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.bib_no || "No Bib"}) - {a.discipline_name || "General"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Assessment Phase / Week
                </label>
                <select
                  value={uploadWeek}
                  onChange={(e) => setUploadWeek(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                >
                  {Array.from({ length: 9 }, (_, i) => i + 1).map((week) => (
                    <option key={week} value={week}>Week {week}{week === 1 ? " (Initial Assessment)" : week === 9 ? " (Final Evaluation)" : ""}</option>
                  ))}
                </select>
              </div>

              <div>
                <FileUploadWithScanner
                  label="Report Document (PDF, Image)"
                  name="uploadFile"
                  accept=".pdf,image/*"
                  required
                  value={uploadFile}
                  onChange={(file) => setUploadFile(file)}
                  scannerTitle="Scan Report Document"
                  targetFileName={`camp_eval_week${uploadWeek}_${Date.now()}.jpg`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Remarks / Observations
                </label>
                <textarea
                  rows={2}
                  value={uploadRemarks}
                  onChange={(e) => setUploadRemarks(e.target.value)}
                  placeholder="e.g. Scanned fitness benchmark sheet signed by trainer..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingReport || !uploadFile}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm disabled:opacity-50"
                >
                  {uploadingReport ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Mode Toggle ── */}
      <div className="flex gap-2 bg-white border border-slate-200 p-1.5 rounded-2xl shadow-sm w-full print-hidden">
        <button
          onClick={() => setViewMode("individual")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
            viewMode === "individual" ? "bg-cyan-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          <FaUser /> Individual Athlete
        </button>
        <button
          onClick={() => setViewMode("all_athletes")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
            viewMode === "all_athletes" ? "bg-indigo-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          <FaTable /> All Athletes Comparison
        </button>
      </div>

      {/* ═══════════════════════════════════════════════
          INDIVIDUAL VIEW
      ═══════════════════════════════════════════════ */}
      {viewMode === "individual" && (
        <>
          {/* Selectors Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm print-hidden">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                Select Athlete
              </label>
              <select
                value={selectedAthleteId}
                onChange={(e) => setSelectedAthleteId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {athletes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} (BIB: {a.bib_no}) — {a.discipline_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                Assessment Phase
              </label>
              <div className="flex gap-2">
                {Array.from({ length: 9 }, (_, i) => i + 1).map((wk) => (
                  <button
                    key={wk}
                    type="button"
                    onClick={() => setActiveWeek(wk)}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                      activeWeek === wk
                        ? "bg-cyan-700 text-white shadow-sm ring-2 ring-cyan-400"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                    }`}
                  >
                    Week {wk}{wk === 1 ? ": Initial" : wk === 9 ? ": Final" : ""}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Athlete Profile Card */}
          {selectedAthlete && (
            <div className="bg-gradient-to-r from-cyan-50 to-indigo-50 border border-cyan-200 rounded-2xl p-4 shadow-sm flex items-center gap-4 print-hidden">
              <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-cyan-300 shrink-0 bg-white flex items-center justify-center">
                {photoUrl ? (
                  <img src={photoUrl} alt={selectedAthlete.name} className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <FaUser className="text-2xl text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-slate-900 text-base">{selectedAthlete.name}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  BIB: <span className="font-bold text-cyan-800 font-mono">{selectedAthlete.bib_no}</span>
                  {selectedAthlete.discipline_name && <> · {selectedAthlete.discipline_name}</>}
                  {selectedAthlete.category && <> · <span className="font-semibold text-indigo-700">{selectedAthlete.category}</span></>}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {selectedAthlete.district && <>District: {selectedAthlete.district}</>}
                  {selectedAthlete.father_name && <> · Father: {selectedAthlete.father_name}</>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Phase</div>
                <div className={`text-sm font-black mt-0.5 ${activeWeek === 1 ? "text-emerald-700" : "text-purple-700"}`}>
                  Week {activeWeek} — {activeWeek === 1 ? "Initial" : "Final"}
                </div>
              </div>
            </div>
          )}

          {/* Side-by-side: Form + Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Entry Form */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4 print-hidden">
              <div className="pb-3 border-b border-slate-200">
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FaStopwatch className="text-cyan-700" />
                  Record Week {activeWeek} Performance
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {activeWeek === 1 ? "Initial Benchmark" : activeWeek === 9 ? "Final Evaluation" : `Week ${activeWeek} Progress Assessment`}
                </p>
              </div>

              <form onSubmit={handleSave} className="space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {testDefinitions.map((test) => (
                    <div key={test.key} className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <label className="block text-slate-900 font-bold mb-0.5">{test.label}</label>
                      <span className="text-[10px] text-slate-500 block mb-1">{test.hint}</span>
                      <input
                        type="text"
                        placeholder={test.placeholder}
                        value={formData[test.key] || ""}
                        onChange={(e) => handleChange(test.key, e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm"
                      />
                    </div>
                  ))}
                </div>
                {customAllottedGames.length > 0 && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-3">
                    <div className="mb-3">
                      <h3 className="text-xs font-black uppercase tracking-wider text-amber-950">
                        Allotted Game Trials
                      </h3>
                      <p className="mt-0.5 text-[10px] text-amber-800">
                        These camp-only games are not covered by the standard weekly tests. Enter this week&apos;s timing, distance, weight, or score.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {customAllottedGames.map((game) => {
                        const trial = Array.isArray(formData.game_trials)
                          ? formData.game_trials.find((item) => String(item?.game_name || "").toLowerCase() === game.game_name.toLowerCase())
                          : null;
                        const metric = [game.measurement_type, game.measurement_unit].filter(Boolean).join(" / ");
                        return (
                          <div key={game.game_name} className="rounded-xl border border-amber-200 bg-white p-3">
                            <label className="block font-bold text-slate-900">{game.game_name}</label>
                            <span className="mb-1 block text-[10px] text-slate-500">
                              {metric || "Enter the measured performance"}
                            </span>
                            <input
                              type="text"
                              value={trial?.value || ""}
                              onChange={(event) => handleCustomGameTrialChange(game.game_name, event.target.value)}
                              placeholder={game.measurement_unit ? `Result in ${game.measurement_unit}` : "Enter result"}
                              className="w-full rounded-lg border border-amber-300 bg-amber-50/40 px-2.5 py-1.5 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[10px]">
                    Coach Assessment Remarks
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Overall observations, future focus areas..."
                    value={formData.remarks || ""}
                    onChange={(e) => handleChange("remarks", e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-xs"
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-3 bg-cyan-700 hover:bg-cyan-800 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 transition-all uppercase tracking-wider"
                >
                  <FaSave /> {saving ? "Saving..." : `Save Week ${activeWeek} Assessment`}
                </button>
              </form>
            </div>

            {/* Comparison Table */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <div className="pb-3 border-b border-slate-200 mb-4">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FaChartLine className="text-cyan-700" /> Week 1 vs Week 9 Comparison
                </h3>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-7 h-7 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !comparisonData?.comparison ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No comparison data available yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-slate-800">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-2.5">Test</th>
                        <th className="p-2.5 text-center">Week 1</th>
                        <th className="p-2.5 text-center">Week 9</th>
                        <th className="p-2.5 text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {comparisonData.comparison.map((row) => (
                        <tr key={row.field} className="hover:bg-slate-50/80">
                          <td className="p-2.5 font-semibold text-slate-900 text-[11px]">{row.test}</td>
                          <td className="p-2.5 text-center font-mono text-slate-600">{row.week1}</td>
                          <td className="p-2.5 text-center font-mono text-slate-600">{row.week9}</td>
                          <td className="p-2.5 text-right font-bold">
                            {row.improved === "improved" ? (
                              <span className="text-emerald-700 flex items-center justify-end gap-1">
                                <FaArrowUp className="text-[10px]" /> {row.improvement}
                              </span>
                            ) : row.improved === "declined" ? (
                              <span className="text-rose-700 flex items-center justify-end gap-1">
                                <FaArrowDown className="text-[10px]" /> {row.improvement}
                              </span>
                            ) : row.improved === "equal" ? (
                              <span className="text-cyan-700 flex items-center justify-end gap-1">
                                <FaMinus className="text-[10px]" /> {row.improvement}
                              </span>
                            ) : (
                              <span className="text-slate-400">{row.improvement}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Signatures */}
              <div className="mt-6 pt-4 border-t border-slate-200 grid grid-cols-3 gap-4 text-center text-xs text-slate-700">
                <div>
                  <div className="h-8 border-b border-dashed border-slate-300 mb-1" />
                  <p className="font-bold">Assistant Coach</p>
                  <p className="text-[10px] text-slate-500">Signature</p>
                </div>
                <div>
                  <div className="h-8 border-b border-dashed border-slate-300 mb-1" />
                  <p className="font-bold">Camp In-Charge</p>
                  <p className="text-[10px] text-slate-500">Signature</p>
                </div>
                <div>
                  <div className="h-8 border-b border-dashed border-slate-300 mb-1" />
                  <p className="font-bold">Official</p>
                  <p className="text-[10px] text-slate-500">Signature</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════
          ALL ATHLETES PIVOT TABLE VIEW
      ═══════════════════════════════════════════════ */}
      {viewMode === "all_athletes" && (
        <div className="space-y-4 print-hidden">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FaUsers className="text-indigo-700" /> All Athletes — Benchmark Comparison
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Rows = Athletes, Columns = Test Events (Week 1 → Week 9)
                </p>
              </div>
            </div>

            {loadingAll ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-[11px] text-slate-800 min-w-[900px]">
                  <thead className="bg-slate-800 text-white text-[10px] uppercase tracking-wider font-bold">
                    <tr>
                      <th className="p-3 sticky left-0 bg-slate-800 z-10 min-w-[140px]">Athlete / BIB</th>
                      {testDefinitions.map((t) => (
                        <th key={t.key} className="p-2 text-center min-w-[80px]" colSpan={2}>
                          {t.label.split(" – ")[0].split(" —")[0]}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-slate-700 text-[9px]">
                      <th className="p-2 sticky left-0 bg-slate-700 z-10">Name</th>
                      {testDefinitions.map((t) => (
                        <React.Fragment key={t.key}>
                          <th className="p-1.5 text-center text-emerald-300">W1</th>
                          <th className="p-1.5 text-center text-purple-300">W9</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pivotAthletes.length === 0 ? (
                      <tr>
                        <td colSpan={1 + testDefinitions.length * 2} className="p-8 text-center text-slate-400">
                          No athletes found.
                        </td>
                      </tr>
                    ) : (
                      pivotAthletes.map((ath, idx) => (
                        <tr key={ath.id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                          <td className="p-2.5 sticky left-0 bg-inherit z-10 border-r border-slate-200">
                            <div className="font-bold text-slate-900 text-xs">{ath.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{ath.bib_no}</div>
                          </td>
                          {testDefinitions.map((t) => (
                            <React.Fragment key={t.key}>
                              <td className="p-2 text-center font-mono text-slate-700 bg-emerald-50/40 text-[11px]">
                                {getVal(week1Records, ath.id, t.key)}
                              </td>
                              <td className="p-2 text-center font-mono text-slate-700 bg-purple-50/40 text-[11px]">
                                {getVal(week9Records, ath.id, t.key)}
                              </td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          PRINT-ONLY LAYOUT
      ═══════════════════════════════════════════════ */}
      <div className="print-only">
        {viewMode === "individual" && selectedAthlete && comparisonData ? (
          <div className="print-page" style={{ fontFamily: "sans-serif", background: "#fff", color: "#000", padding: "0" }}>
            <PrintOrgHeader campName={activeCamp?.name} venue={activeCamp?.venue} />

            {/* Athlete Profile */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", border: "1px solid #1e293b", borderRadius: "6px", padding: "10px", marginBottom: "12px" }}>
              <div style={{ width: "72px", height: "84px", border: "2px solid #1e293b", borderRadius: "4px", overflow: "hidden", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {photoUrl ? <img src={photoUrl} alt={selectedAthlete.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: "9px", color: "#94a3b8" }}>Photo</span>}
              </div>
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", fontSize: "11px" }}>
                {[
                  ["Player Name", selectedAthlete.name],
                  ["Father's Name", selectedAthlete.father_name || "N/A"],
                  ["BIB Number", selectedAthlete.bib_no || "N/A"],
                  ["District", selectedAthlete.district || "N/A"],
                  ["Discipline", selectedAthlete.discipline_name || "Para Athletics"],
                  ["Category", selectedAthlete.category || "N/A"],
                ].map(([label, value]) => (
                  <div key={label}><span style={{ fontWeight: "700", color: "#475569" }}>{label}: </span><span style={{ fontWeight: "600" }}>{value}</span></div>
                ))}
              </div>
            </div>

            {/* Comparison Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", border: "2px solid #1e293b" }}>
              <thead>
                <tr style={{ background: "#e2e8f0", fontWeight: "900", borderBottom: "2px solid #1e293b" }}>
                  {["Performance Test", "Week 1 (Initial)", "Week 9 (Final)", "Improvement"].map((h) => (
                    <th key={h} style={{ border: "1px solid #1e293b", padding: "6px 8px", textAlign: "center" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(comparisonData?.comparison || []).map((row) => (
                  <tr key={row.field} style={{ borderBottom: "1px solid #94a3b8", textAlign: "center" }}>
                    <td style={{ border: "1px solid #94a3b8", padding: "5px 8px", fontWeight: "700", textAlign: "left" }}>{row.test}</td>
                    <td style={{ border: "1px solid #94a3b8", padding: "5px 8px", fontFamily: "monospace" }}>{row.week1 || "—"}</td>
                    <td style={{ border: "1px solid #94a3b8", padding: "5px 8px", fontFamily: "monospace" }}>{row.week9 || "—"}</td>
                    <td style={{ border: "1px solid #94a3b8", padding: "5px 8px", fontWeight: "700", color: row.improved === "improved" ? "#15803d" : row.improved === "declined" ? "#b91c1c" : "#0891b2" }}>
                      {row.improvement || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Signatures */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", textAlign: "center", fontSize: "10px", marginTop: "24px", paddingTop: "16px", borderTop: "2px solid #1e293b" }}>
              <div>
                <div style={{ borderTop: "1px solid #1e293b", paddingTop: "6px", fontWeight: "700" }}>Assistant Coach</div>
                <div style={{ fontSize: "9px", color: "#64748b" }}>Signature</div>
              </div>
              <div>
                <div style={{ borderTop: "1px solid #1e293b", paddingTop: "6px", fontWeight: "700" }}>Camp In-Charge</div>
                <div style={{ fontSize: "9px", color: "#64748b" }}>Signature</div>
              </div>
              <div>
                <div style={{ borderTop: "1px solid #1e293b", paddingTop: "6px", fontWeight: "700" }}>Official</div>
                <div style={{ fontSize: "9px", color: "#64748b" }}>Signature</div>
              </div>
            </div>
          </div>
        ) : viewMode === "all_athletes" ? (
          <div className="print-page" style={{ fontFamily: "sans-serif", background: "#fff", color: "#000" }}>
            <PrintOrgHeader campName={activeCamp?.name} venue={activeCamp?.venue} />
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", border: "2px solid #1e293b" }}>
              <thead>
                <tr style={{ background: "#1e293b", color: "#fff", fontWeight: "900" }}>
                  <th style={{ border: "1px solid #1e293b", padding: "5px 6px", textAlign: "left" }}>Athlete / BIB</th>
                  {testDefinitions.map((t) => (
                    <th key={t.key} style={{ border: "1px solid #1e293b", padding: "4px", textAlign: "center" }} colSpan={2}>
                      {t.label.split(" – ")[0].split(" —")[0]}
                    </th>
                  ))}
                </tr>
                <tr style={{ background: "#334155", color: "#fff", fontSize: "8px" }}>
                  <th style={{ border: "1px solid #334155", padding: "3px 6px" }}>Name</th>
                  {testDefinitions.map((t) => (
                    <React.Fragment key={t.key}>
                      <th style={{ border: "1px solid #334155", padding: "3px", textAlign: "center", color: "#86efac" }}>W1</th>
                      <th style={{ border: "1px solid #334155", padding: "3px", textAlign: "center", color: "#d8b4fe" }}>W9</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivotAthletes.map((ath, idx) => (
                  <tr key={ath.id} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #cbd5e1" }}>
                    <td style={{ border: "1px solid #cbd5e1", padding: "4px 6px", fontWeight: "700" }}>
                      {ath.name}<br /><span style={{ fontWeight: "500", color: "#64748b", fontFamily: "monospace", fontSize: "8px" }}>{ath.bib_no}</span>
                    </td>
                    {testDefinitions.map((t) => (
                      <React.Fragment key={t.key}>
                        <td style={{ border: "1px solid #cbd5e1", padding: "4px", textAlign: "center", fontFamily: "monospace" }}>{getVal(week1Records, ath.id, t.key)}</td>
                        <td style={{ border: "1px solid #cbd5e1", padding: "4px", textAlign: "center", fontFamily: "monospace" }}>{getVal(week9Records, ath.id, t.key)}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CoachCampReports;
